<script>
  import {getContext, onMount} from 'svelte';
  import {combineLatest} from 'rxjs';
  import {map} from 'rxjs/operators';
  import {fixLink$} from '@/store/lang';
  import {LINKS, PATHS} from '@/const';
  import {memeMarket$, memeQuery$} from '@/streams/config/MemeX';
  import {coinUtil, envUtil, numUtil} from '@/helper';
  import Button from '@/widgets/Button.svelte';
  import {contractUtil} from '@/helper/contract';
  import SkeletonLine from '@/widgets/skeleton/SkeletonLine.svelte';
  import SkeletonBasicView from '@/widgets/skeleton/BasicView.svelte';
  import TrendImage from '@/modules/home/components/TrendImageNew.svelte';
  import {contractMarkets$, spotMarkets$} from '@/modules/markets/streams-market';
  import {hotContracts$, hotSpots$} from '@/modules/homepage/streams';
  import {isMobileSize$} from '@/streams/window-inner-size';
  import {topGainers$} from '@/modules/markets/streams-ranking';
  import {MARKET_TAB_TYPE} from '@/modules/homepage/constants';
  import {marketUtil} from '@/modules/markets/util2';
  import lang from '../HomePage.lang';

  export let legality = false

  const LG = getContext('LG');
  const isMobile = envUtil.isMobile;
  const HOT_DATA_LIST_COUNT = isMobile ? 4 : 6;
  const MMX_DATA_LIST_COUNT = isMobile ? 1 : 2;

  const marketsMap = {
    SPOT: {
      marketsId: 0,
      sortKey: 'combinationTurnoverEv',
      viewMoreLink: $fixLink$(PATHS.MARKETS.pathname),
    },
    CONTRACT: {
      marketsId: 2,
      sortKey: 'combinationTurnoverEv',
      viewMoreLink: $fixLink$(PATHS.MARKETS.pathname) + '?tabType=Contract',
    },
    NEW_LISTING: {
      marketsId: 1,
      sortKey: 'listTime',
      viewMoreLink: $fixLink$(PATHS.MARKETS.pathname + '?tabType=NewListings'),
    },
    GAINER: {
      marketsId: 0,
      sortKey: 'changePercent',
      viewMoreLink: $fixLink$(PATHS.MARKETS.pathname + '?tabType=Spot&sort=changePercent'),
    },
    MEMEX: {
      marketsId: 0,
      sortKey: 'priceChangePct24h',
      viewMoreLink: $fixLink$(PATHS.MARKETS.pathname + '?tabType=Spot&sort=changePercent'),
    },
  };

  let spotBackupList = [];
  let contractBackupList = [];
  let activeTab = MARKET_TAB_TYPE.CONTRACT;

  $: spotBackupList = genData(HOT_DATA_LIST_COUNT, MARKET_TAB_TYPE.SPOT, [...$spotMarkets$]);
  $: contractBackupList = genData(HOT_DATA_LIST_COUNT, MARKET_TAB_TYPE.CONTRACT, [...$contractMarkets$]);
  $: mmxMarkets = $memeMarket$.slice(0, MMX_DATA_LIST_COUNT);

  const hotData$ = combineLatest([spotMarkets$, hotSpots$, contractMarkets$, hotContracts$, isMobileSize$, topGainers$, memeMarket$]).pipe(
    map(([spotMarkets, spots, contractMarkets, contracts, isMobileSize, topGainers, memeMarket]) => {
      let spotList = spots.map((s) => spotMarkets.find((m) => m.symbol === s)).filter(Boolean);
      let contractList = contracts.map((s) => contractMarkets.find((m) => m.symbol === s)).filter(Boolean);
      if (spotList.length < HOT_DATA_LIST_COUNT) {
        spotList = composeArray(spotList, spotBackupList).slice(0, HOT_DATA_LIST_COUNT);
      }
      if (contractList.length < HOT_DATA_LIST_COUNT) {
        contractList = composeArray(contractList, contractBackupList).slice(0, HOT_DATA_LIST_COUNT);
      }
      const newListingMarkets = marketUtil.sortWithListTime([...contractMarkets, ...spotMarkets]);

      if (isMobileSize) {
        return {
          [MARKET_TAB_TYPE.SPOT]: spotList.slice(0, HOT_DATA_LIST_COUNT),
          [MARKET_TAB_TYPE.CONTRACT]: contractList.slice(0, HOT_DATA_LIST_COUNT),
          [MARKET_TAB_TYPE.GAINER]: topGainers,
          [MARKET_TAB_TYPE.NEW_LISTING]: newListingMarkets.slice(0, HOT_DATA_LIST_COUNT),
          [MARKET_TAB_TYPE.MEMEX]: memeMarket,
        };
      }
      return {
        [MARKET_TAB_TYPE.SPOT]: spotList.slice(0, HOT_DATA_LIST_COUNT),
        [MARKET_TAB_TYPE.CONTRACT]: contractList.slice(0, HOT_DATA_LIST_COUNT),
        [MARKET_TAB_TYPE.NEW_LISTING]: newListingMarkets.slice(0, HOT_DATA_LIST_COUNT),
      };
    })
  );

  onMount(() => {
    // 赋值mmx的参数，获取 mmx/Gainers 列表
    memeQuery$.next({
      orderBy: 'Gainers',
      pageNum: 1,
      // 取大，主要给header mmx搜索拿全量数据使用
      pageSize: 10
    });

    const container = document.querySelector(".right");
    const highlightBlock = document.querySelector(".highlight-block");
    if (container && highlightBlock) {
      animateHighlightBlock(container, highlightBlock);
    }
  });

  function composeArray(list1, list2) {
    const ret = [];
    [...list1, ...list2].forEach((item) => {
      if (!ret.find(({symbol}) => item.symbol === symbol)) {
        ret.push(item);
      }
    });
    return ret;
  }

  function genData(length, tab, ...rest) {
    const map = marketsMap[tab];
    const markets = rest.length > 1 ? rest[map.marketsId] : rest[0];
    return markets ? markets.sort((a, b) => b[map.sortKey] - a[map.sortKey]).slice(0, length) : [];
  }

  function tradeClickHandler(data, ismmx = false) {
    return function (e) {
      e.stopPropagation();
      if (ismmx) {
        location.href = LINKS.PHEMEX_MEME_TRADE
      } else {
        window.open(data.tradeLink, '_blank', 'noopener');
      }
    };
  }

  function tabClickHandler(event) {
    const tab = event.target.dataset.tab;
    if (!tab) return;
    activeTab = tab;
  }

  function viewMoreClickHandler() {
    window.open(marketsMap[activeTab].viewMoreLink, '_self', 'noopener');
  }

  function getRandomPosition(maxX, maxY) {
    const x = Math.random() * (maxX - 300); // 防止超出容器
    const y = Math.random() * (maxY - 300);
    return { x, y };
  }

  function animateHighlightBlock(container, highlightBlock) {
    const containerRect = container.getBoundingClientRect();
    const maxX = containerRect.width;
    const maxY = containerRect.height;

    function moveBlock() {
      const { x, y } = getRandomPosition(maxX, maxY);
      highlightBlock.style.transition = "transform 10s ease-in-out";
      highlightBlock.style.transform = `translate(${x}px, ${y}px)`;

      // 在动画结束后再次触发移动
      setTimeout(moveBlock, 10000); // 动画时长为 20s
    }

    moveBlock(); // 开始第一次移动
  }
</script>

<style lang="scss">
  .gap-20 {
    gap: 20px;
  }
  .gap-4 {
    gap: 4px;
  }
  .right {
    margin-left: 60px;
    width: 536px;
    box-sizing: border-box;
    .c-gray {
      color: #888b94;
    }
    :global(.glass-block) {
      border-radius: 24px;
      padding: 20px;
      font-weight: 500;
      font-size: 14px;
      background: rgba(49, 50, 51, 0.4);
      color: #fff;
      overflow: hidden;
      backdrop-filter: blur(50px);
    }
    .mmx-pair {
      .logo {
        border-radius: 50%;
      }
    }
    .arrow {
      transform: rotate(270deg) translateX(-1px);
    }
    .main-market {
      padding-left: 0;
      padding-right: 0;
    }
    .tab {
      margin-top: 0;
      margin-bottom: 0;
      line-height: 24px;
      color: #888b94;
      font-weight: 500;
      transition: color 0.2s ease-in-out;
    }
    .tab.active {
      color: #fff;
      font-weight: 700;
    }
    .view-more-btn-wrap {
      margin-right: -32px;
    }
    .view-more-btn-wrap :global(button) {
      line-height: 24px;
      height: 24px;
      border-width: 0;
      color: #888B94;
    }
    .row {
      height: 44px;
      margin-top: 12px;
    }
    .row:hover,
    .crypto:hover {
      background: rgba(255,255,255,0.05);
    }
    .cell {
      padding-left: 40px;
    }
    .cell:first-child {
      padding-left: 24px;
    }
    .cell:last-child {
      padding-right: 24px;
    }
    .up {
      color: #32F469;
    }
    .down {
      color: #f61a14;
    }
    .cell-pair {
      width: 140px;
    }
    .c-gray {
      color: #888B94
    }
    .table-header {
      margin-bottom: 12px;
    }
    .cell-110 {
      width: 110px;
    }
    .cell-124 {
      width: 124px;
    }
    .cell-val-pair {
      text-align: left;
      min-width: 220px;
    }
    .cell-val-close {
      width: 140px;
      text-align: left;
    }
    .cell-val-changepercent {
      text-align: right;
    }
  }
  .highlight-block {
    position: absolute;
    width: 200px;
    height: 200px;
    background: linear-gradient(135deg, #3040E4 0%, #5700e4 23%, #0a0c10 41%, #30e2ed 66%, #3ceaf3 100%); /* 高亮块颜色 */
    border-radius: 50%;
    filter: blur(30px);
    opacity: 0.6;
    animation: glow 3s infinite alternate;
  }
  .mmx-market {
    min-height: 124px;
  }
  .tab-wrap {
    font-weight: 500;
  }
  .img-wrap {
    width: 32px;
    height: 32px;
    background: #2c2c2c;
    border-radius: 50%;
  }
  .img-wrap > img {
    object-fit: contain;
  }
  @keyframes glow {
    0% {
      filter: blur(30px);
    }
    100% {
      filter: blur(60px);
    }
  }
  @keyframes shine {
    0% {
      box-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
    }
    50% {
      box-shadow: 0 0 40px rgba(255, 255, 255, 0.8);
    }
    100% {
      box-shadow: 0 0 60px rgba(255, 255, 255, 1);
    }
  }
  @media screen and (max-width: 640px) {
    .right {
      width: 100%;
      margin-left: 0;
      margin-top: 20px;
      .tab {
        font-size: 14px;
        font-weight: 500;
      }
      .cell-val-pair {
        min-width: 180px;
      }
      .img-wrap {
        width: 24px;
        height: 24px;
      }
    }
  }
</style>

<div class="pr f1 df fdc gap-20 right ovh">
  <div class="highlight-block" />
  {#if !legality}
    <div class="glass-block main-market" on:click|stopPropagation>
      <div class="title-wrap df aic jcsb ph24">
        <div class="tab-wrap df aic" on:click={tabClickHandler}>
          <h3 class="tab f16 cp" class:active={activeTab === MARKET_TAB_TYPE.CONTRACT} data-tab={MARKET_TAB_TYPE.CONTRACT}>{LG(lang.HotContract)}</h3>
          <h3 class="tab ml24 f16 cp" class:active={activeTab === MARKET_TAB_TYPE.SPOT} data-tab={MARKET_TAB_TYPE.SPOT}>{LG(lang.HotSpot)}</h3>
          <h3 class="tab ml24 f16 cp" class:active={activeTab === MARKET_TAB_TYPE.NEW_LISTING} data-tab={MARKET_TAB_TYPE.NEW_LISTING}>{LG(lang.NewListhings)}</h3>
        </div>
        <div class="view-more-btn-wrap c-gray f14">
          <Button type="text" on:click={viewMoreClickHandler}><i class="iconfont arrow dib fw1">&#xe623;</i></Button>
        </div>
      </div>
      <SkeletonBasicView loaded={$hotData$ && $hotData$[activeTab] && $hotData$[activeTab].length === HOT_DATA_LIST_COUNT}>
        <svelte:fragment slot="skeleton">
          <div class="df fdcr ml24 mr24 mr12 skeleton-group">
            {#each new Array(HOT_DATA_LIST_COUNT).fill(0) as item}
              <SkeletonLine class="mt12" height="44px" childProps={envUtil.isMobile ? [
              {width: '32px', height: '32px', borderRadius: '50%', baseColor: '#333', highlightColor: '#090a0a'},
              {width: '120px', baseColor: '#333', highlightColor: '#090a0a'},
              {width: '70px', class: 'grid-end', baseColor: '#333', highlightColor: '#090a0a'},
              {width: '70px', class: 'grid-end', baseColor: '#333', highlightColor: '#090a0a'},
            ]: [
              {width: '32px', height: '32px', borderRadius: '50%', baseColor: '#333', highlightColor: '#090a0a'},
              {width: '150px', baseColor: '#333', highlightColor: '#090a0a'},
              {width: '90px', class: 'grid-end', baseColor: '#333', highlightColor: '#090a0a'},
              {width: '90px', class: 'grid-end', baseColor: '#333', highlightColor: '#090a0a'},
              {width: '70px', class: 'grid-end', baseColor: '#333', highlightColor: '#090a0a'},
            ]}></SkeletonLine>
            {/each}
          </div>
        </svelte:fragment>
        <div class="container">
          {#if $hotData$ && $hotData$[activeTab]}
            {#each $hotData$[activeTab] as data}
              <div class="row df aic jcsb ph24 cp" on:click={tradeClickHandler(data, false)}>
                <div class="df aic jcfs fw2 f1 lh34 cell-val-pair">
                  <div class="img-wrap mr12">
                    <img width="32" height="32" src={coinUtil.getUrl(data.baseCurrency)} alt={data.displaySymbol} />
                  </div>
                  {#if contractUtil.isContract(data.type)}
                    <div>
                      <div class="lh20 fw2 ooo">
                        <span>{data.symbolV2}</span>
                        <span class="cell-type xs:dn">{LG(lang.Perpetual)}</span>
                      </div>
                    </div>
                  {:else}
                    {data.displaySymbol}
                  {/if}
                </div>
                <div class="lh34 cell-val-close">${numUtil.delimit(data.close)}</div>
                <div class="df aic cell-110 lh34 xs:dn">
                  <TrendImage width={100} height={22} data={data.klineData} change={data.change24hEp} />
                </div>
                <div class="cell-124 lh34 cell-val-changepercent">
                  <span class:up={data.changePercent > 0} class:down={data.changePercent < 0} class="br4 lh34 cell-124">
                    {data.changePercentStr}
                  </span>
                </div>
              </div>
            {/each}
          {/if}
        </div>
      </SkeletonBasicView>
    </div>
    <div class="mmx-market">
      <SkeletonBasicView loaded={mmxMarkets.length === MMX_DATA_LIST_COUNT}>
        <svelte:fragment slot="skeleton">
          <div class="df gap-20 skeleton-group">
            {#each new Array(MMX_DATA_LIST_COUNT).fill(0) as item}
              <SkeletonLine class="mt12 f1 pl20 glass-block" height="44px" childProps={[
                {width: '32px', height: '32px', borderRadius: '50%', baseColor: '#333', highlightColor: '#090a0a'},
                {width: '170px', baseColor: '#333', highlightColor: '#090a0a'},
              ]}>
              </SkeletonLine>
            {/each}
          </div>
        </svelte:fragment>
        <div class="df gap-20">
          {#each mmxMarkets as market}
            <div class="f1 glass-block" on:click={tradeClickHandler(market, true)}>
              <div class="df aifs gap-4 cp mmx-pair">
                <div class="f1 df">
                  <div class="df aic jcc ovh img-wrap">
                    <img width="32" height="32" src={market.logoUrl || ''} onerror="this.src='https://img.phemex.com/v1/a4afa886-3400-4962-859d-bba075cc0570/defult-coin.svg'" alt={market.fullNamefullName} />
                  </div>
                  <div class="ml8">
                    <div class="f14 fw2">
                      <span>{market.coin}</span>
                      <span class="ml4 fw1" class:up={Number(market.priceChangePct24h) > 0} class:down={Number(market.priceChangePct24h) < 0}
                      >{`${(Number(market.priceChangePct24h) > 0 && '+') || (Number(market.priceChangePct24h) < 0 && '-') || ''}${market.priceChangePct24hDisplay}`}</span
                      >
                    </div>
                    <span class="fw1 f12 c-gray">{market.ccy?.slice(0, 4) + '****' + market.ccy?.slice(-4)}</span>
                  </div>
                </div>
                <i class="iconfont arrow dib fw1 c-gray">&#xe623;</i>
              </div>
              <div class="df mt12">
                <div class="f1 df fdc gap-4">
                  <span class="f12 fw1 c-gray">{LG(lang.LastPrice)}</span>
                  <span class="f14 fw2">${numUtil.delimit(market.price || 0) || '0.00'}</span>
                </div>
                <div class="f1 df fdc gap-4">
                  <span class="f12 fw1 c-gray">{LG(lang.MACP)}</span>
                  <span class="f14 fw2">{numUtil.abbreviateNumber(market.marketCap || '0.00')}</span>
                </div>
              </div>
            </div>
          {/each}
        </div>
      </SkeletonBasicView>
    </div>
  {/if}
</div>
